import { Link } from "react-router-dom";
import { useState } from "react";
import { ThumbsUp, Heart, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Character card with:
 *  - Tap the image → open character detail
 *  - Tap the heart (top-right) → toggle favorite (private save)
 *  - Tap the like count (bottom-left) → toggle upvote (public, drives trending)
 *  - Tap the @creator chip (bottom-right) → visit their public profile
 * `onEngagementChange({like_count, is_liked, is_favorited})` lets the parent
 * (e.g. Discover, Favorites) update the row in-place without re-fetching.
 */
export default function CharacterCard({ character, testIdPrefix = "char", onEngagementChange }) {
  const [liked, setLiked] = useState(!!character.is_liked);
  const [likeCount, setLikeCount] = useState(character.like_count || 0);
  const [favorited, setFavorited] = useState(!!character.is_favorited);
  const [busy, setBusy] = useState(false);

  function stop(e) { e.preventDefault(); e.stopPropagation(); }

  async function toggleLike(e) {
    stop(e);
    if (busy) return;
    setBusy(true);
    // Optimistic
    const nextLiked = !liked;
    const nextCount = likeCount + (nextLiked ? 1 : -1);
    setLiked(nextLiked); setLikeCount(nextCount);
    try {
      const { data } = nextLiked
        ? await api.post(`/characters/${character.id}/like`)
        : await api.delete(`/characters/${character.id}/like`);
      setLiked(!!data.is_liked);
      setLikeCount(data.like_count);
      onEngagementChange?.({ like_count: data.like_count, is_liked: data.is_liked, is_favorited: favorited });
    } catch (err) {
      // Rollback
      setLiked(!nextLiked); setLikeCount(likeCount);
      toast.error(err.response?.data?.detail || "Could not update like.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleFavorite(e) {
    stop(e);
    if (busy) return;
    setBusy(true);
    const nextFav = !favorited;
    setFavorited(nextFav);
    try {
      const { data } = nextFav
        ? await api.post(`/characters/${character.id}/favorite`)
        : await api.delete(`/characters/${character.id}/favorite`);
      setFavorited(!!data.is_favorited);
      onEngagementChange?.({ like_count: likeCount, is_liked: liked, is_favorited: data.is_favorited });
      if (nextFav) toast.success("Added to favorites.");
    } catch (err) {
      setFavorited(!nextFav);
      toast.error(err.response?.data?.detail || "Could not update favorite.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid={`${testIdPrefix}-card-${character.id}`}
      className="character-card group relative block overflow-hidden rounded-2xl bg-[#16141D] border border-white/5"
    >
      <Link to={`/character/${character.id}`} className="block">
        <div className="aspect-[3/4] w-full overflow-hidden">
          {character.avatar_url ? (
            <img
              src={character.avatar_url}
              alt={character.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-violet-950 to-neutral-900">
              <span className="font-display text-4xl text-white/40">{character.name?.[0]}</span>
            </div>
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0A10] via-[#0B0A10]/50 to-transparent pointer-events-none" />
        {character.is_nsfw && (
          <span className="absolute top-3 left-3 nsfw-badge">18+</span>
        )}
        <div className="absolute inset-x-0 bottom-0 p-4 pb-11 pointer-events-none">
          <h3 className="font-display text-xl leading-tight text-white line-clamp-1">
            {character.name}
          </h3>
          {character.tagline && (
            <p className="mt-1 text-xs text-neutral-300 line-clamp-2 leading-snug">
              {character.tagline}
            </p>
          )}
        </div>
      </Link>

      {/* Favorite (heart) — top-right corner */}
      <button
        data-testid={`${testIdPrefix}-card-${character.id}-favorite`}
        onClick={toggleFavorite}
        disabled={busy}
        aria-label={favorited ? "Unfavorite" : "Favorite"}
        className={`absolute top-2 right-2 z-10 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur-md transition ${
          favorited
            ? "bg-rose-500/90 text-white shadow-lg shadow-rose-500/30"
            : "bg-black/40 text-white/80 hover:bg-black/60"
        }`}
      >
        <Heart size={16} fill={favorited ? "currentColor" : "none"} strokeWidth={2} />
      </button>

      {/* Bottom action row — like on the left, creator on the right.
          Both use the SAME height/padding/font so they stay aligned on
          narrow mobile widths (e.g. 2-up grid at 390px viewport). Creator
          username truncates to prevent overlapping the like button. */}
      <div className="absolute inset-x-3 bottom-3 z-10 flex items-center justify-between gap-2 pointer-events-none">
        <button
          data-testid={`${testIdPrefix}-card-${character.id}-like`}
          onClick={toggleLike}
          disabled={busy}
          aria-label={liked ? "Unlike" : "Like"}
          className={`pointer-events-auto inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11px] backdrop-blur transition ${
            liked
              ? "bg-violet-500/90 text-white"
              : "bg-black/50 text-neutral-200 hover:bg-black/70"
          }`}
        >
          <ThumbsUp size={12} fill={liked ? "currentColor" : "none"} />
          <span data-testid={`${testIdPrefix}-card-${character.id}-like-count`}>{likeCount}</span>
        </button>

        {character.creator_username && character.creator_id !== "system" && (
          <Link
            to={`/u/${character.creator_username}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testIdPrefix}-card-${character.id}-creator`}
            className="pointer-events-auto inline-flex min-w-0 max-w-[55%] items-center gap-1 rounded-full bg-black/50 px-2 py-1 text-[11px] text-neutral-200 backdrop-blur hover:bg-black/70 hover:text-white"
          >
            {character.creator_is_plus && (
              <Sparkles
                size={9}
                className="shrink-0 text-violet-300"
                data-testid={`${testIdPrefix}-card-${character.id}-creator-plus`}
              />
            )}
            <span className="truncate">@{character.creator_username}</span>
          </Link>
        )}
      </div>
    </div>
  );
}
